export const HIDDEN_TYPES_STORAGE_KEY = "scheduling.exceptions.hiddenTypes";

export const DEFAULT_HIDDEN_TYPES: string[] = ["late_risk"];

export interface ExceptionRow {
  job: number;
  exception_type: string;
  job_order_name: string;
  job_part_name: string;
  job_thumbnail_file?: string | null;
}

export interface JobGroup {
  jobId: number;
  job_order_name: string;
  job_part_name: string;
  job_thumbnail_file: string | null;
  types: string[];
  counts: Record<string, number>;
  summary: string;
  total: number;
  /** Every exception on the job, ignoring the type filter. */
  allTypes: string[];
  allTotal: number;
}

export function formatType(v: string) {
  return v.replace(/_/g, " ").toUpperCase();
}

export function parseHiddenTypes(raw: string | null): string[] {
  if (raw === null) return [...DEFAULT_HIDDEN_TYPES];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.every((t) => typeof t === "string")) {
      return parsed;
    }
  } catch {
    // fall through to default
  }
  return [...DEFAULT_HIDDEN_TYPES];
}

export function countByType(exceptions: ExceptionRow[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const ex of exceptions) {
    counts[ex.exception_type] = (counts[ex.exception_type] || 0) + 1;
  }
  return counts;
}

/** Groups exceptions by job; jobs whose exceptions are all hidden are dropped. */
export function groupVisibleByJob(
  exceptions: ExceptionRow[],
  hiddenTypes: string[]
): JobGroup[] {
  const hidden = new Set(hiddenTypes);
  const map = new Map<number, JobGroup>();

  for (const ex of exceptions) {
    let group = map.get(ex.job);
    if (!group) {
      group = {
        jobId: ex.job,
        job_order_name: ex.job_order_name,
        job_part_name: ex.job_part_name,
        job_thumbnail_file: ex.job_thumbnail_file ?? null,
        types: [],
        counts: {},
        summary: "",
        total: 0,
        allTypes: [],
        allTotal: 0,
      };
      map.set(ex.job, group);
    }
    const t = ex.exception_type;
    group.allTotal += 1;
    if (!group.allTypes.includes(t)) group.allTypes.push(t);
    if (hidden.has(t)) continue;
    group.counts[t] = (group.counts[t] || 0) + 1;
    group.total += 1;
  }

  const groups: JobGroup[] = [];
  for (const group of map.values()) {
    if (group.total === 0) continue;
    group.types = Object.keys(group.counts);
    group.summary = group.types
      .map((t) => `${group.counts[t]} ${formatType(t).toLowerCase()}`)
      .join(", ");
    groups.push(group);
  }
  return groups;
}
